/**
 * Social + Personal-MP analytics — the ONE module every social capture on WEB
 * goes through. No component calls `posthog.capture` for a `social_*` /
 * `personal_mp_*` event directly; they all come here.
 *
 * Types, prop enums, buckets, and share-attribution ref/channel parsers moved
 * to `@flashka/shared/social/analytics` so mobile can share the event contract
 * verbatim. This file owns the WEB transport layer only:
 *   - `captureSocial` via the `posthog-js` singleton,
 *   - the `sessionStorage`-backed share attribution store,
 *   - `useShareAttributionCapture` mounted inside PostHogProvider.
 *
 * ### Web-specific rules
 *   - `captureSocial` is fire-and-forget and never throws — analytics can
 *     never surface into the feature path.
 *   - PostHog is client-side only here (see `src/providers/posthog.tsx`), so
 *     this module talks to the `posthog-js` singleton directly and works from
 *     non-React call sites too (e.g. `accruePersonalSessionMp`).
 */

import {
  parseShareAttributionChannel,
  parseShareAttributionRef,
  type ProfileDialogTrigger,
  type ShareAttributionChannel,
  type ShareAttributionRef,
  type SocialAnalyticsEvent,
  type SocialAnalyticsEventProps,
  type SocialErrorScope,
  type SuggestionTier,
} from '@flashka/shared/social/analytics';

import type { PostHog } from 'posthog-js';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { useAuth } from '@/auth/better/session';

// Re-export the shared analytics contract so existing web imports of
// `@/lib/social/analytics` continue to resolve. New consumers can import from
// `@flashka/shared/social/analytics` directly.
export * from '@flashka/shared/social/analytics';

/**
 * PostHog's person-property envelope. Allowed alongside the typed props so a
 * capture can also stamp a `$set_once` person property (e.g.
 * `socialFirstFollowAt` on the first follow) without a second round trip.
 */
type PersonPropsEnvelope = {
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
};

/**
 * Capture one social event. Never throws, never awaits, and is a no-op when
 * PostHog isn't initialised (SSR, tests, a missing `VITE_POSTHOG_KEY`).
 */
export function captureSocial<E extends SocialAnalyticsEvent>(
  event: E,
  props?: SocialAnalyticsEventProps[E] & PersonPropsEnvelope,
): void {
  try {
    posthog?.capture?.(event, props);
  } catch {
    /* analytics only — never surface into the feature path */
  }
}

/**
 * Sugar for `social_profile_dialog_shown`. The dialog itself can't know which
 * path opened it, so all four OPEN call sites go through here instead of
 * duplicating the capture shape.
 */
export function captureProfileDialogShown(
  mode: 'complete' | 'edit',
  trigger: ProfileDialogTrigger,
  shownCount?: number,
): void {
  captureSocial('social_profile_dialog_shown', {
    mode,
    trigger,
    ...(shownCount === undefined ? {} : { shownCount }),
  });
}

/**
 * Sugar for `social_suggestions_shown` — the impression denominator for
 * "follow rate per suggestion tier". Rows WITHOUT a `tier` (a producer that
 * predates the field) are counted in `count` but in no tier bucket, so the
 * tier counts never over-claim.
 */
export function captureSuggestionsShown(
  surface: 'profileCarousel' | 'findMatesIdle',
  rows: readonly { tier?: SuggestionTier }[],
): void {
  try {
    const byTier = [0, 0, 0, 0];
    for (const row of rows) {
      if (row.tier && row.tier >= 1 && row.tier <= 4) byTier[row.tier - 1] += 1;
    }
    captureSocial('social_suggestions_shown', {
      surface,
      count: rows.length,
      tier1Count: byTier[0],
      tier2Count: byTier[1],
      tier3Count: byTier[2],
      tier4Count: byTier[3],
    });
  } catch {
    /* analytics only */
  }
}

/** Sugar for the `social_error` event. */
export function captureSocialError(
  scope: SocialErrorScope,
  details?: { endpoint?: string; status?: number; code?: string },
): void {
  captureSocial('social_error', { scope, ...details });
}

/**
 * Report a failed social API call. `endpoint` MUST be a path TEMPLATE
 * (`/api/social/search`, `/api/social/profile/:locator`) — never a real URL
 * with query values, which would leak the searched text.
 */
export function reportSocialApiError(endpoint: string, status?: number): void {
  captureSocialError('api', { endpoint, ...(status === undefined ? {} : { status }) });
}

// ---------------------------------------------------------------------------
// Share attribution — `?ref=` / `?ch=` → $set_once person properties
// ---------------------------------------------------------------------------

const ATTRIBUTION_STORAGE_KEY = 'flashka.social.attribution.v1';

type StoredAttribution = {
  ref: ShareAttributionRef;
  channel?: ShareAttributionChannel;
};

/**
 * Remember the share attribution of THIS landing for the rest of the browser
 * session, so a visitor who signs up minutes later still gets credited to the
 * link that brought them in. sessionStorage (not localStorage) on purpose:
 * attribution belongs to the visit, and a later organic visit must not inherit
 * it.
 *
 * CALL THIS ONLY FOR A SIGNED-OUT LANDING (see `useShareAttributionCapture`).
 * Anything stored here eventually becomes `socialAcquiredViaShare` on a person,
 * and a signed-in landing is not an acquisition — it is an existing user
 * opening a mate's link.
 *
 * Returns `true` when this is a NEW landing for the session (nothing stored
 * yet, or a different link), which is what makes the landing event fire once
 * per visit instead of once per page load. Storage being unavailable counts as
 * new: better a duplicate event than a lost landing.
 */
export function storeShareAttribution(attribution: StoredAttribution): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return true;
    const previous = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    const next = JSON.stringify(attribution);
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, next);
    return previous !== next;
  } catch {
    /* private mode / storage full — attribution is best-effort */
    return true;
  }
}

function readShareAttribution(): StoredAttribution | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    const ref = parseShareAttributionRef(parsed.ref);
    if (!ref) return null;
    return { ref, channel: parseShareAttributionChannel(parsed.channel) };
  } catch {
    return null;
  }
}

/**
 * Route-agnostic share-link capture. Mounted in `PHProvider` (inside the
 * router, ABOVE every route guard) rather than in the profile page, because
 * `/profile/:locator` is behind `ProtectedRoute`: a SIGNED-OUT share-link
 * recipient — the whole point of `?ref` — never mounts that page, so anything
 * living there would lose the attribution at the sign-in wall.
 *
 * Split of duties with `profile.tsx`:
 *   - Storing attribution happens HERE, for a signed-OUT landing only — it
 *     must survive the auth wall and outlive this page load, but it must NOT
 *     credit the viral loop for a user who was already signed in.
 *   - `social_share_link_landed` fires HERE only for a signed-OUT landing,
 *     where `targetResolved` is knowably `false` (the visitor sees the sign-in
 *     wall, never the profile). For a signed-IN landing the profile page fires
 *     it instead, because only it knows whether the target actually resolved.
 *
 * A signed-out visitor who then signs up therefore produces TWO landing events
 * across the journey — the pre-auth bounce (`isSignedIn: false`) and the
 * post-auth arrival (`isSignedIn: true`). They are distinct facts; filter on
 * `isSignedIn` to count either one. Within a single visit the event never
 * double-fires: storage dedupes the bounce, and a per-mount guard dedupes the
 * arrival.
 */
export function useShareAttributionCapture(): void {
  const [searchParams] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();

  const ref = parseShareAttributionRef(searchParams.get('ref'));
  const channel = parseShareAttributionChannel(searchParams.get('ch'));
  const lang = searchParams.get('lang');

  useEffect(() => {
    if (!ref) return;
    // Wait for the session, and store NOTHING until it says signed-OUT. Two
    // separate bugs live on the other side of this line:
    //
    //   1. ATTRIBUTION. `socialAcquiredViaShare` is `$set_once`, i.e. it only
    //      guarantees "I won't overwrite" — never "I only stamp new users". So
    //      storing on an already-signed-in landing credited the viral loop for
    //      every EXISTING user who opened a mate's shared profile (and for
    //      everyone who clicked their own link to check it). A landing that
    //      happens while signed in has nothing to attribute: that person was
    //      already acquired.
    //   2. THE LANDING EVENT. `isLoaded` is false on the first render of any
    //      cold load (it is `!isPending` off a real session round trip), so an
    //      unconditional store ran on that pass, returned "new landing" into a
    //      code path that had already bailed on `!isLoaded`, and left the
    //      second pass comparing identical JSON — `isNewLanding: false`, event
    //      swallowed. The signed-out `social_share_link_landed` therefore never
    //      fired on a cold landing, which is the one landing that matters.
    //
    // Storing later is safe: `ProtectedRoute` renders the SignIn card IN PLACE,
    // so the URL keeps `?ref`/`?ch` across the auth wall, and `isLoaded` is in
    // this effect's deps — it re-runs with the params still readable.
    if (!isLoaded || isSignedIn) return;
    const isNewLanding = storeShareAttribution({ ref, channel });
    if (!isNewLanding) return;
    captureSocial('social_share_link_landed', {
      ref,
      isSignedIn: false,
      // `targetResolved` is OMITTED, not `false`: from up here we genuinely
      // can't see whether the locator would have resolved, and guessing would
      // make broken-link rates unreadable. The signed-in path reports it.
      ...(channel ? { channel } : {}),
      ...(lang ? { lang } : {}),
    });
  }, [ref, channel, lang, isLoaded, isSignedIn]);
}

/**
 * Promote a stored attribution onto the (now identified) person as
 * `$set_once` properties. Idempotent by definition — `$set_once` keeps the
 * FIRST value — so calling this on every identified load is safe and means a
 * visitor→signup conversion gets attributed without any server plumbing.
 */
export function promoteShareAttribution(ph: PostHog | null | undefined): void {
  try {
    const stored = readShareAttribution();
    if (!stored) return;
    ph?.setPersonProperties?.(undefined, {
      socialAcquiredViaShare: true,
      socialAcquisitionRef: stored.ref,
      ...(stored.channel ? { socialAcquisitionChannel: stored.channel } : {}),
    });
  } catch {
    /* analytics only */
  }
}
